<template>
  <div class="cxw">
    <div v-if="!ready" class="cxw-note">{{ missing }}</div>
    <component
      :is="inner"
      v-else
      v-bind="bound"
      @drill-entity="$emit('drill-entity', $event)"
      @select-patient="$emit('select-patient', $event)"
    />
  </div>
</template>

<script>
import { markRaw } from 'vue';

const MISSING = 'This widget needs a metric. Open it from a metric page, or set metric_id in its settings.';

export default {
  name: 'ContextWidget',
  props: {
    inputs: { type: Object, default: () => ({}) },
    config: { type: Object, default: () => ({}) },
    context: { type: Object, default: null },
    width: { type: Number, default: 0 },
    height: { type: Number, default: 0 },
  },
  emits: ['drill-entity', 'select-patient'],
  data() {
    return { inner: null, loadFailed: false };
  },
  computed: {
    scope() {
      const ctx = this.context || {};
      const cfg = this.config || {};
      const metricIds = cfg.metric_id ? [Number(cfg.metric_id)] : (ctx.metricIds || []);
      const domainIds = cfg.performance_domain_id ? [Number(cfg.performance_domain_id)] : (ctx.performanceDomainIds || []);
      return {
        metricIds,
        metricId: metricIds.length ? metricIds[0] : null,
        performanceDomainIds: domainIds,
        performanceDomainId: domainIds.length ? domainIds[0] : null,
        isDomain: !cfg.metric_id && !!ctx.isDomain,
        year: Number(cfg.year) || ctx.year || new Date().getFullYear(),
        entityId: ctx.entityId == null ? null : ctx.entityId,
      };
    },
    hasScope() {
      return this.scope.metricIds.length > 0 || this.scope.performanceDomainIds.length > 0;
    },
    ready() {
      return !!this.inner && this.hasScope;
    },
    missing() {
      if (!this.hasScope) return MISSING;
      return this.loadFailed ? `No component ${this.$options.contextName} is registered.` : 'Loading…';
    },
    bound() {
      const declared = this.inner && this.inner.props ? this.inner.props : {};
      const names = Array.isArray(declared) ? declared : Object.keys(declared);
      const merged = { ...this.scope, ...(this.config.settings || {}) };
      return Object.fromEntries(Object.entries(merged).filter(([key]) => names.includes(key)));
    },
  },
  watch: {
    hasScope: { immediate: true, handler(on) { if (on) this.load(); } },
  },
  methods: {
    async load() {
      if (this.inner) return;
      const { getComponent } = await import('@/config/configurableComponentRegistry');
      const found = getComponent(this.$options.contextName);
      this.loadFailed = !found;
      this.inner = found ? markRaw(found) : null;
    },
  },
};
</script>

<style scoped>
.cxw { width: 100%; height: 100%; overflow: auto; }
.cxw-note { padding: 16px; font-size: 12.5px; color: #8b877f; }
</style>
